import { Sub, RefLink, P, Pill, ApiBlock, FlowSteps } from "./GuideHelpers";
import { cosmoQuestions } from "./constants";

export const SectionDataSourcesCosmo = () => (
  <>
    <Sub id="ds-content-scorecard">7. Content Scorecard — COSMO Evaluation Engine</Sub>
    <P>
      The Content Scorecard is an AI-powered audit system that evaluates how well an Amazon listing
      answers COSMO's <strong>15 shopper-intent questions</strong>. The user must run the scorecard
      to receive a score — no score is displayed by default.
    </P>

    <Sub>System Workflow</Sub>
    <P>
      Two surfaces trigger the same engine: the standalone tool at <code className="text-xs bg-surface-tint-medium px-1 py-0.5 rounded">/spi</code> (user
      supplies any ASIN) and the <strong>Content Scorecard</strong> tab inside Product Detail (ASIN, title,
      and brand are passed in automatically). From there the work is fully automated.
    </P>
    <FlowSteps
      items={[
        { actor: "user", label: "Enter ASIN (standalone) or open the Content Scorecard tab on a product" },
        { actor: "signal", label: "Assemble context: ASIN, product title, brand" },
        { actor: "signal", label: "Call the ai-generate edge function (Lovable AI Gateway → Gemini 3 Flash) with the COSMO system prompt and the 15-question rubric" },
        { actor: "signal", label: "LLM returns structured JSON: per-question score + status + finding + recommendation + evidence snippet, plus a 3-paragraph strategic summary" },
        { actor: "signal", label: "Backend normalizes scores, computes the 5 dimension averages, the overall score, and the letter grade" },
        { actor: "signal", label: "Render the scorecard (dimension breakdown, 15 expandable question rows, strategic recommendations for Listings, A+ Content, Brand Store)" },
        { actor: "user", label: "Review findings and optionally click Generate Recommended Content to draft fixes" },
      ]}
    />
    <P>
      Today the LLM evaluates the listing from the ASIN, title, and brand context — full title, bullets,
      description, A+ content, and image OCR are listed below as the <em>target</em> input set once the
      Amazon listing-retrieval step is wired up. Scoring rubric and formulas already run against whatever
      input the model is given.
    </P>

    <Sub>Required Inputs (target)</Sub>
    <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground mb-3">
      <li>ASIN</li>
      <li>Title</li>
      <li>Bullets</li>
      <li>Description</li>
      <li>A+ Content</li>
      <li>Image Text / OCR (if available)</li>
      <li>Backend Fields (optional)</li>
    </ul>

    <Sub>The 15 COSMO Questions</Sub>
    <div className="overflow-x-auto rounded-sm border border-border my-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-surface-tint-medium">
            <th className="text-left px-3 py-2 font-semibold text-foreground border-b border-border w-8">#</th>
            <th className="text-left px-3 py-2 font-semibold text-foreground border-b border-border">Question</th>
            <th className="text-left px-3 py-2 font-semibold text-foreground border-b border-border w-40">Relation Type</th>
          </tr>
        </thead>
        <tbody className="text-muted-foreground">
          {cosmoQuestions.map(([num, q, rel], i) => (
            <tr key={num} className={i % 2 === 0 ? "bg-card" : "bg-surface-tint-subtle"}>
              <td className="px-3 py-1.5 border-b border-border/50 font-mono text-foreground">{num}</td>
              <td className="px-3 py-1.5 border-b border-border/50">{q}</td>
              <td className="px-3 py-1.5 border-b border-border/50 font-mono text-xs">{rel}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <Sub>Scoring Model</Sub>
    <P>Each of the 15 questions receives a score from 0–100, calculated as:</P>
    <ApiBlock>
      <div className="text-foreground">question_score =</div>
      <div className="pl-4">(explicitness × 0.40) +</div>
      <div className="pl-4">(evidence_strength × 0.30) +</div>
      <div className="pl-4">(coverage_quality × 0.20) +</div>
      <div className="pl-4">(field_diversity × 0.10)</div>
    </ApiBlock>

    <Sub>Status Thresholds</Sub>
    <div className="flex gap-3 flex-wrap mb-3">
      <Pill variant="green">80–100 = Strong</Pill>
      <Pill variant="default">55–79 = Moderate</Pill>
      <Pill variant="orange">40–54 = Weak</Pill>
      <Pill variant="muted">0–39 = Missing</Pill>
    </div>

    <Sub>Dimension Groupings</Sub>
    <ul className="list-disc pl-5 space-y-1 text-sm text-muted-foreground mb-3">
      <li><strong className="text-foreground">Core Usage & Capability</strong> — avg(Q1–Q4)</li>
      <li><strong className="text-foreground">Product Identity & Role</strong> — avg(Q5–Q6)</li>
      <li><strong className="text-foreground">Context</strong> — avg(Q7–Q9)</li>
      <li><strong className="text-foreground">Audience & Persona</strong> — avg(Q10–Q14)</li>
      <li><strong className="text-foreground">Compatibility & Pairing</strong> — Q15</li>
    </ul>

    <Sub>Overall Score</Sub>
    <P>
      The overall score is the straight average of all 15 question scores (each question contributes
      equally). Dimension averages are shown alongside for readability but do not re-weight the total.
    </P>

    <Sub>Grade Mapping</Sub>
    <div className="flex gap-3 flex-wrap mb-3">
      <Pill variant="green">85–100 = A</Pill>
      <Pill variant="default">70–84 = B</Pill>
      <Pill variant="orange">55–69 = C</Pill>
      <Pill variant="muted">40–54 = D</Pill>
      <Pill variant="muted">0–39 = F</Pill>
    </div>

    <Sub>Implementation</Sub>
    <ApiBlock>
      <div>Edge function: <span className="text-foreground">supabase/functions/ai-generate</span></div>
      <div>Provider: <span className="text-foreground">Lovable AI Gateway</span></div>
      <div>Model: <span className="text-foreground">google/gemini-3-flash-preview</span></div>
      <div>Client wrapper: <span className="text-foreground">src/lib/aiScorecard.ts → generateAiScorecard()</span></div>
      <div>Response mode: <span className="text-foreground">JSON (response_format: json_object)</span></div>
    </ApiBlock>

    <P>
      See the two surfaces that run this engine: the <RefLink to="spi">standalone Content Scorecard</RefLink> (any
      ASIN) and the <RefLink to="content-scorecard-tab">Content Scorecard tab inside Product Detail</RefLink> (Hero
      ASIN with title and brand pre-filled).
    </P>
  </>
);
